import { useEffect, useState ,forwardRef, useImperativeHandle} from "react";
import { format, subWeeks, startOfWeek, addDays, subDays, differenceInDays, startOfMonth, subMonths, endOfMonth, addWeeks, addMonths } from "date-fns";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme file
import {
  Popover,
  Button,
  Box,
  TextField,
  IconButton,
  Menu, 
  MenuItem
} from "@mui/material";
import useGlobalContext from "../../context/useGlobalContext";
import "./CustomDatePicker.css";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { matchPath, useLocation } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Calendar from "../../assets/common/calendar.svg";
import ChevronDown from "../../assets/common/chevron-down.svg";
import ChevronUp from "../../assets/common/chevron-up.svg";
import { normalizeToFullDay } from "../../utils/utils";

export type CustomDatePickerHandle = {
  openDropdown: () => void;
};

type DateRange = {
  startDate: string;
  endDate: string;
};

type CustomDatePickerProps = {
  customDateRange?: string | DateRange; // e.g. "lastThreeMonths", "lastTwoMonths", "today"
  isSlideOverOpen?: boolean;
  disabled?: boolean;
  setCustomDateRange?: (range: DateRange) => void;
};
const CustomDatePicker = forwardRef<CustomDatePickerHandle, CustomDatePickerProps>(({customDateRange, isSlideOverOpen, disabled, setCustomDateRange}, ref) => {
  const location = useLocation();
  const {pathname} = useLocation();
  const isHomePage = location.pathname.includes("/home");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { setStartEndMeetingTime, dateRange, setDateRange, selectedPreset, setSelectedPreset } = useGlobalContext();
  const [shouldClosePopover, setShouldClosePopover] = useState<boolean>(false);
  const [tempDateRange, setTempDateRange] = useState(() =>
    typeof customDateRange === "object"
      ? { startDate: new Date(customDateRange.startDate), endDate: new Date(customDateRange.endDate) }
      : dateRange
  );
  const [isCustomSelected, setIsCustomSelected] = useState(false);
  const [isCustomTextField, setIsCustomTextField] = useState(false);
  const [view, setView] = useState("day"); // Track the current view (year, month, day)
  const nextYear = new Date().getFullYear() + 1;
    const shouldHideFilters = !!(
        matchPath({ path: "/", end: true }, pathname) ||
        matchPath({ path: "/home", end: true }, pathname) ||
        matchPath({ path: "/client", end: true }, pathname)
      ) && !isSlideOverOpen;

  const isDisabled = disabled || shouldHideFilters;
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;

  useEffect(() => {
  if (typeof customDateRange === "object" && customDateRange.startDate && customDateRange.endDate) {
    setTempDateRange({
      startDate: new Date(customDateRange.startDate),
      endDate: new Date(customDateRange.endDate),
    });
  }
}, [customDateRange]);

  useEffect(() => {
    setStartEndMeetingTime({
      start_time: format(dateRange.startDate, "yyyy-MM-dd'T'00:00:00xxx"),
      end_time: format(dateRange.endDate, "yyyy-MM-dd'T'23:59:59xxx"),
    });
  }, [dateRange]);
  
  const closePopover = () => {
    setAnchorEl(null);
    // Apply tempDateRange only if a preset was selected
    setDateRange(tempDateRange);
  };  

  const handleDateSelection = (preset : any) => {
    const today = new Date();
    let startDate, endDate;
  
    switch (preset) {
      case "today":
        startDate = new Date();
        endDate = new Date();
        break;
      case "currentWeek":
        startDate = startOfWeek(today, { weekStartsOn: 0 });
        endDate = addDays(startDate, 6);
        break;
  
      case "currentMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
  
      case "lastWeek":
        startDate = startOfWeek(subWeeks(today, 1), { weekStartsOn: 0 });
        endDate = addDays(startDate, 6);
        break;
  
      case "lastMonth":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
          
      case "lastThreeMonths":
        startDate = subMonths(today, 3);
        endDate = today;
        break;
  
      case "custom":
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
        setIsCustomSelected(true);
        setSelectedPreset("custom");
        setTempDateRange({ startDate, endDate });
        setShouldClosePopover(true);
        return; // This return is required to open the date picker to select date range for the user.
      
      default:
        return;
    }
  
    setTempDateRange({ startDate, endDate });
    setSelectedPreset(preset);
    setIsCustomSelected(false);
    setShouldClosePopover(true);
    const normalized = normalizeToFullDay(startDate, endDate);

    if (setCustomDateRange) {
      setCustomDateRange({
        startDate: normalized.startDate.toISOString(),
        endDate: normalized.endDate.toISOString(),
      });
    }
};

useEffect(() => {
  if (isSlideOverOpen) {
    if (typeof customDateRange === "string") {
      handleDateSelection(customDateRange);
    } else if (customDateRange?.startDate && customDateRange?.endDate) {
      const { startDate, endDate } = customDateRange;
      setTempDateRange({
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      });
      setDateRange({
        startDate: new Date(startDate),
        endDate: new Date(endDate),
      });
      setSelectedPreset("custom");
      setIsCustomSelected(true);
    }
  }
}, [isSlideOverOpen, customDateRange]);

useEffect(() => {
  if(shouldClosePopover){
    setShouldClosePopover(false);
    closePopover();
  }
}, [shouldClosePopover]);

  // Inject styles dynamically using useEffect
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .rdrStartEdge, .rdrEndEdge {
        background: #B902CA !important;
      }
  
      .rdrInRange {
        background: #B902CA80 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleToggleDropdown = (event : any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const open = Boolean(anchorEl);

  const openDropdown = () => {
    const fakeEvent = { currentTarget: document.querySelector(".custom-date-picker") };
    if (fakeEvent.currentTarget) {
      handleToggleDropdown(fakeEvent);
    }
  };
  useImperativeHandle(ref, () => ({ openDropdown }));

  const handlePeriodClick = (direction : string) => {
    setDateRange((prev) => {
      let newStartDate: Date, newEndDate: Date;
      const isPrev = direction === "prev";
      
      if (selectedPreset === "today") {
        newStartDate = isPrev ? subDays(prev.startDate, 1) : addDays(prev.startDate, 1);
        newEndDate = isPrev ? subDays(prev.endDate, 1) : addDays(prev.endDate, 1);
      } else if (["lastWeek", "currentWeek"].includes(selectedPreset)) {
        newStartDate = isPrev
          ? startOfWeek(subWeeks(prev.startDate, 1), { weekStartsOn: 0 })
          : startOfWeek(addWeeks(prev.startDate, 1), { weekStartsOn: 0 });
        newEndDate = addDays(newStartDate, 6);
      } else if (["lastMonth", "currentMonth"].includes(selectedPreset)) {
        newStartDate = isPrev
          ? startOfMonth(subMonths(prev.startDate, 1))
          : startOfMonth(addMonths(prev.startDate, 1));
        newEndDate = isPrev
          ? endOfMonth(subMonths(prev.startDate, 1))
          : endOfMonth(addMonths(prev.startDate, 1));
      } else if (selectedPreset === "custom") {
        const daysToShift = differenceInDays(prev.endDate, prev.startDate) + 1;
        newStartDate = isPrev ? subDays(prev.startDate, daysToShift) : addDays(prev.startDate, daysToShift);
        newEndDate = isPrev ? subDays(prev.endDate, daysToShift) : addDays(prev.endDate, daysToShift);
      } else if (selectedPreset === "lastThreeMonths") {
        const rangeMonths = 3;
        if (isPrev) {
        newEndDate = subDays(prev.startDate, 1);
        newStartDate = subMonths(newEndDate, rangeMonths);
      } else {
        newStartDate = addDays(prev.endDate, 1);
        newEndDate = addMonths(newStartDate, rangeMonths);
      }
      } else {
        newStartDate = isPrev ? subWeeks(prev.startDate, 1) : addWeeks(prev.startDate, 1);
        newEndDate = addDays(newStartDate, differenceInDays(prev.endDate, prev.startDate));
      }
  
      setTempDateRange({
        startDate: newStartDate,
        endDate: newEndDate
      });
      const normalized = normalizeToFullDay(newStartDate, newEndDate);
      if (setCustomDateRange) {
        setCustomDateRange({
          startDate: normalized.startDate.toISOString(),
          endDate: normalized.endDate.toISOString(),
        });
      }
  
      return {
        ...prev,
        startDate: newStartDate,
        endDate: newEndDate,
      };
    });
  };


  return (
    <Box display="flex" alignItems="center">
      {/* Navigation & Today Button */}
    <Box display="flex" alignItems="center">
      <IconButton 
        className="calendar-nav-button"
        size="small" 
        sx={{ color: "black", fontSize: "24px", mr: 0 }} 
        onClick={() => handlePeriodClick("prev")}   
        disabled={isDisabled}
      >
        <NavigateBeforeIcon sx={{ fontSize: "24px", color: "black", opacity: isDisabled ? 0.2 : 1, }} />
      </IconButton>
      
      
      {/* Input Fields */}
      <Box className={`custom-date-picker ${isDisabled ? "disabled" : ""}`}
       onClick={(event) => {
        if (isDisabled) return;
            handleToggleDropdown(event);
            setIsCustomSelected(false);
          }}>
        <img
          src={Calendar}
          alt="Calendar Icon"
          width={20}
          height={20}
          style={{ opacity: 1, marginRight: 8 }}
        />

        <Box className="custom-date-picker__inputs">
          <TextField
            value={format(dateRange.startDate, "MMM dd, yyyy")}
            variant="outlined"
            size="small"
            className={`date-range-picker__field date-range-picker__field--start`}
            InputProps={{ className: "date-range-picker__input", sx: { fontWeight: "bold", pointerEvents: "none" } }}
          />
          {(
    <>
          <span className="date-range-picker__divider">|</span>
          <TextField
            value={format(dateRange.endDate, "MMM dd, yyyy")}
            variant="outlined"
            size="small"
            className={`date-range-picker__field date-range-picker__field--end`}
            InputProps={{ className: "date-range-picker__input", sx: { fontWeight: "bold", pointerEvents: "none" }, }}
          />
          </>
  )}
        </Box>
        <IconButton
          sx={{ position: "absolute", right: "6px" }}
        >
          {Boolean(anchorEl) ? (
            <img
              src={ChevronUp}
              alt="Chevron Up"
              width={12}
              height={12}
              style={{ filter: "invert(16%) sepia(95%) saturate(7122%) hue-rotate(283deg) brightness(94%) contrast(99%)" }}
            />
          ) : (
            <img
              src={ChevronDown}
              alt="Chevron Down"
              width={12}
              height={12}
              style={{ filter: "invert(16%) sepia(95%) saturate(7122%) hue-rotate(283deg) brightness(94%) contrast(99%)" }}
            />
          )}
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={closePopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right", // Moves menu more to the left
          }}
          PaperProps={{
            sx: {
              mt: 1,
              ml: 40,
              borderRadius: "8px",
              border: "1px solid #ddd",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              width: "320px", // Adjust width
            },
          }}
        >
          <MenuItem onClick={() => handleDateSelection("today")} selected={selectedPreset === "today"}
          className="menu-item"         
          >
            Today
          </MenuItem>
          <MenuItem onClick={() => handleDateSelection("currentWeek")} selected={selectedPreset === "currentWeek"}
          className="menu-item"     
          >
            Current Week
          </MenuItem>
          <MenuItem onClick={() => handleDateSelection("currentMonth")} selected={selectedPreset === "currentMonth"}
          className="menu-item"       
          >
            Current Month
          </MenuItem>
          <MenuItem onClick={() => handleDateSelection("lastWeek")} selected={selectedPreset === "lastWeek"}
          className="menu-item"        
          >
            Last Week
          </MenuItem>
          <MenuItem onClick={() => handleDateSelection("lastMonth")} selected={selectedPreset === "lastMonth"}
          className="menu-item"         
          >
            Last Month
          </MenuItem>        
          {!isHomePage && (
            <MenuItem
              onClick={() => {
                handleDateSelection("custom");
                setIsCustomTextField(true);
              }}
              selected={isCustomSelected && selectedPreset === "custom"}
              className="menu-item"            
            >
              Custom
            </MenuItem>
          )}
        </Menu>
      </Box>
      {/* Custom Date Picker Popper */}
      <Popover
          open={isCustomTextField}
          anchorEl={anchorEl}
          onClose={() => setIsCustomTextField(false)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: { 
              marginLeft: "-20px", // Adjust left positioning if needed
              width: "560px", // Set desired width
              height: "103px", // Set desired height
              padding: 2 // Add some padding for better spacing
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              justifyContent: "flex-end",
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={tempDateRange.startDate}
                disabled={isDisabled}
                onChange={(newValue) => {
                  if (newValue) {
                    setTempDateRange((prev) => ({
                      ...prev,
                      startDate: newValue,
                      endDate: newValue > prev.endDate ? newValue : prev.endDate,
                    }));
                  }
                }}
                format="MMM dd, yyyy"
                minDate={new Date(lastYear, 0, 1)}
                maxDate={new Date(nextYear, 11, 31)}
                views={["year", "month", "day"]}
                onViewChange={(newView) => setView(newView)} // Track view state
                onOpen={() => setView("day")}
                slotProps={{
                  textField: { fullWidth: true, variant: "outlined"},
                  desktopPaper: {
                    className: `date-picker-paper ${view === "year" ? "year-view" : ""}`,
                  },
                }}
              />

              <DatePicker
                label="End Date"
                value={tempDateRange.endDate}
                disabled={isDisabled}
                open={isDisabled ? false : undefined}
                onChange={(newValue) => {
                  if (newValue && newValue >= tempDateRange.startDate) {
                    setTempDateRange((prev) => ({ ...prev, endDate: newValue }));
                  }
                }}
                format="MMM dd, yyyy"
                minDate={tempDateRange.startDate} // Prevents selecting an end date before the start date
                maxDate={new Date(nextYear, 11, 31)}
                views={["year", "month", "day"]}
                onViewChange={(newView) => setView(newView)} // Track view state
                onOpen={() => setView("day")}
                slotProps={{
                  textField: { fullWidth: true, variant: "outlined" },
                  desktopPaper: {
                    className: `date-picker-paper ${view === "year" ? "year-view" : ""}`,
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              onClick={() => {
                setDateRange(tempDateRange);
                setSelectedPreset("custom");
                setIsCustomSelected(true);
                if (setCustomDateRange) {
                  const { startDate, endDate } = normalizeToFullDay(
                    tempDateRange.startDate,
                    tempDateRange.endDate
                  );
                  setCustomDateRange({
                    startDate: startDate.toISOString(),
                    endDate: endDate.toISOString(),
                  });
                }

                setIsCustomTextField(false);
              }}
              size="small"
              className="apply-button"
            >
              Apply
            </Button>
          </Box>
        </Popover>
      <IconButton 
        className="calendar-nav-button"
        size="small" 
        onClick={() => handlePeriodClick("next")} 
        sx={{ color: "black", fontSize: "24px", mr: 1 }} 
        disabled={isDisabled}
      >
      <NavigateNextIcon sx={{ fontSize: "24px", color: "black", opacity: isDisabled ? 0.2 : 1, }} />
        </IconButton>
      </Box>    
    </Box>
  );
});

export default CustomDatePicker;
